import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import Delete from "pages/variant/delete"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { ChevronUpDown } from "components/icons/dynamic"
import useVariantRepositories from "repositories/variant"

export default function List() {
    const { can } = useAuth()

    const { title, transition, setTransition } = useOutletContext()

    const navigate = useNavigate()
    const location = useLocation()

    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const q = searchParams.get('q')

    const sort = searchParams.get('sort')
    const dir = searchParams.get('dir')

    const { data: variantData, isLoading: isLoadingVariantData, mutate: mutateVariantData } = useVariantRepositories({
        page,
        q,
        sort,
        dir
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current?.focus()
    }, [q])

    useEffect(() => {
        if (q === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingVariantData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [variantData, isLoadingVariantData])

    const toggleSort = (value) => {
        if ((dir === null || dir === 'desc') || sort !== value) {
            setSearchParams({
                sort: value,
                dir: 'asc'
            })
        } else {
            setSearchParams({
                sort: value,
                dir: 'desc'
            })
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchParams({ q: e.target.value })} value={q ?? ''} type="text" placeholder={`${lang.search} Variant`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {can('variant.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th scope="col" className="px-6 py-3 whitespace-nowrap">
                                <button className="flex items-center space-x-1 text-xs font-medium text-left uppercase text-neutral-500" onClick={() => toggleSort('name')}>
                                    <span>Nama</span>
                                    <span><ChevronUpDown direction={sort === 'name' ? (dir === 'asc' ? 'up' : 'down') : false} className="w-4 h-4" strokeWidth={2} /></span>
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3 whitespace-nowrap">
                                <button className="flex items-center space-x-1 text-xs font-medium text-left uppercase text-neutral-500" onClick={() => toggleSort('slug')}>
                                    <span>Slug</span>
                                    <span><ChevronUpDown direction={sort === 'slug' ? (dir === 'asc' ? 'up' : 'down') : false} className="w-4 h-4" strokeWidth={2} /></span>
                                </button>
                            </th>
                            <th scope="col" className="px-6 py-3 whitespace-nowrap">
                                <button className="flex items-center space-x-1 text-xs font-medium text-left uppercase text-neutral-500" onClick={() => toggleSort('created_at')}>
                                    <span>{lang.created_at}</span>
                                    <span><ChevronUpDown direction={sort === 'created_at' ? (dir === 'asc' ? 'up' : 'down') : false} className="w-4 h-4" strokeWidth={2} /></span>
                                </button>
                            </th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                        {isLoadingVariantData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.loading_data}
                                </td>
                            </tr>
                        )}

                        {variantData?.data?.length === 0 && !q && !isLoadingVariantData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_data}
                                </td>
                            </tr>
                        )}

                        {variantData?.data?.length === 0 && q && !isLoadingVariantData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_result}
                                </td>
                            </tr>
                        )}

                        {variantData?.data?.length > 0 && variantData.data.map((row) => (
                            <tr key={row.id}>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.name}
                                </td>
                                <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {row.slug}
                                </td>
                                <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {row.created_at ? moment(row.created_at).format('MMMM D, YYYY') : '-'}
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                    <div className="inline-flex items-center space-x-2">
                                        {can('variant.update') && (
                                            <Link to={`${row.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90">
                                                <Pencil className="w-6 h-6" />
                                            </Link>
                                        )}

                                        {can('variant.delete') && (
                                            <Delete data={row} onSuccess={() => mutateVariantData()} />
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination links={variantData?.links} from={variantData?.from} to={variantData?.to} total={variantData?.total} />
        </div>
    )
}
